import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { Fragment, type KeyboardEvent as ReactKeyboardEvent, useEffect } from 'react';

import useDrawerSize from '@/hooks/useDrawerSize';

import { cn } from '../lib/utils';

import type { DrawerProps as Props } from './types';

const SIDES = {
  right: {
    anchor: 'inset-y-0 right-0 h-full w-[35rem] max-w-[94vw]',
    hidden: 'translate-x-full',
    shown: 'translate-x-0'
  },
  left: {
    anchor: 'inset-y-0 left-0 h-full w-[35rem] max-w-[94vw]',
    hidden: '-translate-x-full',
    shown: 'translate-x-0'
  },
  bottom: {
    anchor: 'inset-x-0 bottom-0 h-[50vh] w-full',
    hidden: 'translate-y-full',
    shown: 'translate-y-0'
  }
} as const;

// The handle sits on the edge facing the page; these keys grow or shrink the drawer from it.
const HANDLES = {
  right: {
    className: 'absolute inset-y-0 left-0 w-1.5 cursor-col-resize',
    grow: 'ArrowLeft',
    orientation: 'vertical',
    shrink: 'ArrowRight'
  },
  left: {
    className: 'absolute inset-y-0 right-0 w-1.5 cursor-col-resize',
    grow: 'ArrowRight',
    orientation: 'vertical',
    shrink: 'ArrowLeft'
  },
  bottom: {
    className: 'h-1.5 w-full shrink-0 cursor-row-resize',
    grow: 'ArrowUp',
    orientation: 'horizontal',
    shrink: 'ArrowDown'
  }
} as const;

const RESIZE_STEP = 40;

const isIgnoredEscape = (event: KeyboardEvent | ReactKeyboardEvent, selector?: string) =>
  event.defaultPrevented ||
  (!!selector && !!(event.target as HTMLElement | null)?.closest?.(selector));

/**
 * A panel that slides in from the left, right, or bottom edge of the screen and overlays the
 * page's existing content -- unlike `Modal`, it never dims the content behind
 * it unless `dimmedBackground` asks for it, and by default only closes via its own close button or
 * whatever the caller renders inside `children` (e.g. a form's own Cancel/Save buttons) --
 * there is no click-outside-to-close unless `dismissOnClickOutside` opts in.
 *
 * `title` gives the standard heading-and-close row; `header` replaces that row outright, for
 * drawers whose top is a control rather than a title. `footer` pins content below the scrolling
 * body.
 *
 * By default the drawer is a modal dialog that traps focus. `modal={false}` docks it beside the
 * page instead, so the page stays clickable while it is open; Escape then closes it only while
 * focus is inside the drawer. `resizable` adds a drag handle on the edge facing the page, and
 * `sizeStorageKey` remembers the size the user picks.
 *
 * HeadlessUI's `Dialog` bundles outside-click and Escape into the same
 * `onClose` callback with no way to separate them, so `Dialog`'s own `onClose`
 * only runs when `dismissOnClickOutside` is set, and Escape-to-close is
 * reimplemented by hand below so it always works regardless of that flag --
 * focus trapping is unaffected, since it doesn't depend on `onClose`.
 */
function Drawer({
  open,
  onClose,
  title,
  header,
  footer,
  children,
  className,
  headerClassName,
  bodyClassName,
  dialogClassName,
  dataTestId,
  blurredBackground = false,
  dimmedBackground = false,
  dismissOnClickOutside = false,
  side = 'right',
  modal = true,
  resizable = false,
  sizeStorageKey,
  defaultSize,
  minSize,
  escapeIgnoredSelector,
  ariaLabel
}: Props) {
  const drawerSize = useDrawerSize({ defaultSize, minSize, side, storageKey: sizeStorageKey });

  useEffect(() => {
    if (!open || !modal) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isIgnoredEscape(event, escapeIgnoredSelector)) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [escapeIgnoredSelector, modal, open, onClose]);

  const { anchor, hidden, shown } = SIDES[side];
  const handle = HANDLES[side];
  const sizeStyle = resizable
    ? side === 'bottom'
      ? { height: drawerSize.size }
      : { width: drawerSize.size }
    : undefined;

  const handleResizeKey = (event: ReactKeyboardEvent) => {
    if (event.key !== handle.grow && event.key !== handle.shrink) return;
    // Arrow keys would otherwise also scroll whatever contains the handle.
    event.preventDefault();
    drawerSize.nudge(event.key === handle.grow ? RESIZE_STEP : -RESIZE_STEP);
  };

  const titleClassName = 'text-lg font-semibold text-uniform-gray-900 dark:text-uniform-gray-100';
  const panelClassName = cn(
    'fixed flex flex-col',
    anchor,
    'bg-white shadow-2xl dark:bg-uniform-gray-900',
    side === 'bottom' && 'border-t border-uniform-gray-200 dark:border-uniform-gray-700',
    className
  );

  const content = (
    <>
      {resizable && (
        <div
          role="separator"
          aria-orientation={handle.orientation}
          aria-label="Resize drawer"
          aria-valuemin={drawerSize.minSize}
          aria-valuemax={drawerSize.maxSize}
          aria-valuenow={drawerSize.size}
          tabIndex={0}
          onPointerDown={drawerSize.startResize}
          onKeyDown={handleResizeKey}
          className={cn(
            handle.className,
            'z-10 bg-uniform-gray-100 hover:bg-uniform-gray-300 focus-visible:bg-uniform-gray-300 focus-visible:outline-none dark:bg-uniform-gray-800 dark:hover:bg-uniform-gray-600'
          )}
        />
      )}
      <div
        className={cn(
          'flex flex-shrink-0 items-center justify-between gap-4 border-b border-uniform-gray-200 p-6 dark:border-uniform-gray-700',
          headerClassName
        )}>
        {header ?? (
          <>
            {title &&
              (modal ? (
                <DialogTitle as="h2" className={titleClassName}>
                  {title}
                </DialogTitle>
              ) : (
                <h2 className={titleClassName}>{title}</h2>
              ))}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="shrink-0 rounded p-1 text-uniform-gray-500 transition-colors hover:text-uniform-gray-700 dark:hover:text-uniform-gray-200 [outline:none] focus-visible:ring-2 focus-visible:ring-uniform-gray-400">
              <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
            </button>
          </>
        )}
      </div>

      <div className={cn('min-h-0 flex-1 overflow-y-auto p-6', bodyClassName)}>{children}</div>

      {footer && (
        <div className="flex-shrink-0 border-t border-uniform-gray-200 dark:border-uniform-gray-700">
          {footer}
        </div>
      )}
    </>
  );

  if (!modal) {
    if (!open) return null;
    return (
      <section
        aria-label={ariaLabel ?? (typeof title === 'string' ? title : undefined)}
        data-testid={dataTestId}
        style={sizeStyle}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !isIgnoredEscape(event, escapeIgnoredSelector)) onClose();
        }}
        className={cn('z-30', panelClassName)}>
        {content}
      </section>
    );
  }

  return (
    <Transition show={open} as={Fragment}>
      <Dialog
        as="div"
        className={cn('relative z-30', dialogClassName)}
        data-testid={dataTestId}
        onClose={dismissOnClickOutside ? onClose : () => {}}>
        <div className="fixed inset-0 overflow-hidden">
          {(blurredBackground || dimmedBackground) && (
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0">
              <div
                className={cn(
                  'fixed inset-0',
                  blurredBackground && 'backdrop-blur-sm',
                  dimmedBackground && 'bg-gray-900/25'
                )}
              />
            </TransitionChild>
          )}
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom={hidden}
            enterTo={shown}
            leave="ease-in duration-200"
            leaveFrom={shown}
            leaveTo={hidden}>
            <DialogPanel style={sizeStyle} className={panelClassName}>
              {content}
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}

export { Drawer };
